import React, { useEffect, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useAuth } from '@/hooks/useAuth'
import { useAppSelector } from '@/hooks/redux'
import { authManager } from '@/services/authService'

interface SessionProviderProps {
  children: React.ReactNode
}

const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  const { restoreSession } = useAuth()
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  // Only block rendering if there's no cached session at all.
  // If authManager already has a valid token (app still in memory) or
  // localStorage has cached user data, skip the loading screen entirely.
  const [isRestoring, setIsRestoring] = useState(
    !authManager.isAuthenticated() && !isAuthenticated
  )

  useEffect(() => {
    const initializeSession = async () => {
      // If authManager already has a valid in-memory session (e.g., user
      // clicked a push notification while app was open), skip restore entirely.
      if (authManager.isAuthenticated()) {
        setIsRestoring(false)
        return
      }

      try {
        await restoreSession()
      } catch (error) {
        console.error('Session restoration failed:', error)
      } finally {
        setIsRestoring(false)
      }
    }

    initializeSession()
  }, [restoreSession])

  // Only show loading screen if we have NO cached session data.
  // If localStorage had a cached session (isAuthenticated from redux init),
  // render the app immediately while token refresh happens in the background.
  if (isRestoring && !isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          backgroundColor: 'background.default'
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Restoring your session...
        </Typography>
      </Box>
    )
  }

  return <>{children}</>
}

export default SessionProvider
